/**
 * =============================================================================
 * IDS CRM Web - Daily Van Check section (work-log modals)
 * =============================================================================
 *
 * The Daily Van Check required whenever Work Type is Site Work. Extracted for
 * the scheduled-task work-log modal (2026-08-11): the tasks API has required
 * daily_van_check for site work since R4a, but the task modal never rendered
 * the form — site-work logs on jobs always 400'd and could not be signed off.
 *
 * Self-contained: fetches the active vehicle list itself and owns all seat/
 * damage-photo mechanics; the parent holds only the check object and passes
 * value + onChange. Markup and class names mirror the ticket work-log modal's
 * inline version exactly (TicketDetail.css styles them globally).
 *
 * NOTE: TicketDetail's WorkLogModal still carries its own inline twin of this
 * form (it predates this component and adds edit-mode prefill); migrate it
 * here when that modal is next touched.
 *
 * Saved Log Work (Phase 6 step 7, spec 6.5): an optional uploadAttachment
 * sends each damage photo into the job's saved Log Work on its own, and the
 * check keeps the file reference the server gives back instead of the photo.
 * removeUploadedAttachment takes a sent photo out again. Without them the
 * photos are read into data URLs, as before.
 *
 * =============================================================================
 */

import React, { useState, useEffect, useRef } from 'react';
import { filesToAttachmentPayload } from '../utils/attachments';
import { authedFileHref } from '../utils/files';
import { attachmentFromFileRef, removeSentAttachment, uploadFilesOneByOne } from '../hooks/useTemplateCheckForm';

export const DAILY_VAN_CHECK_BOOLEAN_FIELDS = [
  ['warning_lights_messages', 'Warning lights/messages'],
  ['adblue_required', 'AdBlue required'],
  ['oil_sufficient', 'Oil sufficient'],
  ['screen_wash_sufficient', 'Screen wash sufficient'],
  ['lights_working', 'Lights working'],
  ['tyre_pressure_correct', 'Tyre pressure correct'],
  ['tyre_tread_sufficient', 'Tyre tread sufficient'],
  ['damage', 'Vehicle damage']
];

export const emptyDailyVanCheck = {
  vehicle_id: '',
  seat_selections: [],
  current_odometer_reading: '',
  warning_lights_messages: '',
  warning_notes: '',
  adblue_required: '',
  oil_sufficient: '',
  screen_wash_sufficient: '',
  lights_working: '',
  tyre_pressure_correct: '',
  tyre_tread_sufficient: '',
  damage: '',
  damage_description: '',
  damage_photo_attachments: [],
  additional_information: ''
};

export const getSelectedEmployeeIds = (check) =>
  (check?.seat_selections || []).filter(id => id && id !== '');

/** Pre-submit validation; returns an error message or null. */
export const validateDailyVanCheck = (check) => {
  const selectedIds = getSelectedEmployeeIds(check);
  if (selectedIds.length === 0) {
    return 'At least one employee must be selected';
  }
  if (selectedIds.length !== new Set(selectedIds).size) {
    return 'The same employee cannot be selected for multiple seats';
  }
  if (check.damage === 'true' && (check.damage_photo_attachments || []).length === 0) {
    return 'Damage photo is required when vehicle damage is reported';
  }
  return null;
};

/** API payload shape: yes/no strings become booleans, seats become ids. */
export const buildDailyVanCheckPayload = (check) => ({
  ...check,
  employees_present_user_ids: getSelectedEmployeeIds(check),
  current_odometer_reading: check.current_odometer_reading === ''
    ? ''
    : Number(check.current_odometer_reading),
  ...Object.fromEntries(
    DAILY_VAN_CHECK_BOOLEAN_FIELDS.map(([field]) => [
      field,
      check[field] === '' ? '' : check[field] === 'true'
    ])
  )
});

function DamagePhotoThumb({ att, onRemove }) {
  const photoUrl = att.dataUrl || (att.url?.startsWith('/uploads/') ? authedFileHref(att.url) : att.url) || '';
  return (
    <div className="photo-thumb">
      <a href={photoUrl} target="_blank" rel="noopener noreferrer" title={att.name}>
        <img src={photoUrl} alt={att.name} />
      </a>
      <button type="button" onClick={onRemove}>&times;</button>
    </div>
  );
}

/**
 * @param {object}   props
 * @param {Function} [props.uploadAttachment] Saved Log Work: (file) => Promise<FileRef>, one call per
 *                                         damage photo. The check keeps attachmentFromFileRef(ref).
 * @param {Function} [props.removeUploadedAttachment] Saved Log Work: (attachment) => Promise, for a
 *                                         removed photo that was sent (has a file_id or client_ref).
 */
function DailyVanCheckSection({
  authFetch, value, onChange, engineers, required = true, onError,
  uploadAttachment = null, removeUploadedAttachment = null
}) {
  const [vehicles, setVehicles] = useState([]);
  const [vehiclesLoading, setVehiclesLoading] = useState(false);
  const [sendingPhotos, setSendingPhotos] = useState(0);
  // The latest check, so a photo that finishes sending does not undo a change
  // made while it was on its way.
  const valueRef = useRef(value);
  valueRef.current = value;

  useEffect(() => {
    const fetchVehicles = async () => {
      setVehiclesLoading(true);
      try {
        const response = await authFetch('/api/v1/tickets/active-vehicles');
        if (response.ok) {
          const data = await response.json();
          setVehicles(data.data || []);
        }
      } catch (err) {
        console.error('Failed to load vehicles:', err);
      } finally {
        setVehiclesLoading(false);
      }
    };
    fetchVehicles();
  }, [authFetch]);

  const update = (field, fieldValue) => onChange({ ...value, [field]: fieldValue });

  const selectedVehicle = vehicles.find(v => v.vehicle_id === value.vehicle_id);
  const seatCount = selectedVehicle?.number_of_seats || 0;

  const handleVehicleChange = (vehicleId) => {
    const vehicle = vehicles.find(v => v.vehicle_id === vehicleId);
    const trimmedSeats = (value.seat_selections || []).slice(0, vehicle?.number_of_seats || 0);
    onChange({ ...value, vehicle_id: vehicleId, seat_selections: trimmedSeats });
  };

  const updateSeatSelection = (seatIndex, userId) => {
    const seats = [...(value.seat_selections || [])];
    while (seats.length <= seatIndex) seats.push('');
    seats[seatIndex] = userId;
    onChange({ ...value, seat_selections: seats });
  };

  const isEmployeeAlreadySelected = (userId, currentSeatIndex) =>
    (value.seat_selections || []).some((id, idx) => id === userId && idx !== currentSeatIndex);

  // Damage photos APPEND — a phone camera delivers one photo per picker
  // visit, so replacing the array would cap the check at a single photo.
  const handleDamagePhotoChange = async (files, inputEl) => {
    if (uploadAttachment) {
      const picked = Array.from(files || []);
      if (inputEl) inputEl.value = '';
      setSendingPhotos(count => count + picked.length);
      const errors = await uploadFilesOneByOne(picked, file => uploadAttachment(file), ref => {
        const latest = valueRef.current || {};
        const next = {
          ...latest,
          damage_photo_attachments: [...(latest.damage_photo_attachments || []), attachmentFromFileRef(ref)]
        };
        valueRef.current = next;
        onChange(next);
      });
      setSendingPhotos(count => Math.max(0, count - picked.length));
      if (errors.length > 0 && onError) onError(errors.join('; '));
      return;
    }
    try {
      const attachments = await filesToAttachmentPayload(files);
      onChange({
        ...value,
        damage_photo_attachments: [...(value.damage_photo_attachments || []), ...attachments]
      });
    } catch (err) {
      if (onError) onError(err.message);
    } finally {
      if (inputEl) inputEl.value = '';
    }
  };

  const removeDamagePhoto = (index) => {
    const removed = (value.damage_photo_attachments || [])[index];
    if (removeUploadedAttachment && removed && (removed.file_id || removed.client_ref)) {
      removeSentAttachment(removeUploadedAttachment, removed, message => { if (onError) onError(message); });
    }
    onChange({
      ...value,
      damage_photo_attachments: (value.damage_photo_attachments || []).filter((_, i) => i !== index)
    });
  };

  return (
    <div className="form-section daily-van-check-section">
      <div className="form-section-header">
        <h3>Daily Van Check</h3>
        <p>Required when Work Type is Site Work. Full detail is stored against the vehicle history.</p>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="dvc_vehicle_id">Vehicle *</label>
          <select
            id="dvc_vehicle_id"
            value={value.vehicle_id}
            onChange={event => handleVehicleChange(event.target.value)}
            required={required}
          >
            <option value="">{vehiclesLoading ? 'Loading vehicles...' : 'Select vehicle...'}</option>
            {vehicles.map(vehicle => (
              <option key={vehicle.vehicle_id} value={vehicle.vehicle_id}>
                {vehicle.registration_number} ({vehicle.number_of_seats} seats)
                {vehicle.current_odometer_reading !== null && vehicle.current_odometer_reading !== undefined
                  ? ` - ${Number(vehicle.current_odometer_reading).toLocaleString()} mi`
                  : ''}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label htmlFor="dvc_current_odometer_reading">Current odometer reading *</label>
          <input
            id="dvc_current_odometer_reading"
            type="number"
            min="0"
            step="1"
            value={value.current_odometer_reading}
            onChange={event => update('current_odometer_reading', event.target.value)}
            required={required}
          />
        </div>
      </div>

      <div className="form-group">
        <label>Employees travelling *</label>
        {!value.vehicle_id ? (
          <div className="modal-hint">Select a vehicle to choose employees travelling.</div>
        ) : (
          <div className="seat-selection-grid">
            {Array.from({ length: seatCount }, (_, idx) => (
              <div key={idx} className="seat-selection-row">
                <label htmlFor={`dvc_seat_${idx}`}>Seat {idx + 1}</label>
                <select
                  id={`dvc_seat_${idx}`}
                  value={value.seat_selections?.[idx] || ''}
                  onChange={event => updateSeatSelection(idx, event.target.value)}
                  className={isEmployeeAlreadySelected(value.seat_selections?.[idx], idx) ? 'duplicate-warning' : ''}
                >
                  <option value="">Empty</option>
                  {engineers.map(engineer => (
                    <option
                      key={engineer.user_id}
                      value={engineer.user_id}
                      disabled={isEmployeeAlreadySelected(engineer.user_id, idx)}
                    >
                      {engineer.full_name}
                      {isEmployeeAlreadySelected(engineer.user_id, idx) ? ' (already selected)' : ''}
                    </option>
                  ))}
                </select>
              </div>
            ))}
            {getSelectedEmployeeIds(value).length === 0 && (
              <div className="modal-hint warning">At least one employee must be selected.</div>
            )}
          </div>
        )}
      </div>

      <div className="van-check-grid">
        {DAILY_VAN_CHECK_BOOLEAN_FIELDS.map(([field, label]) => (
          <div key={field} className="form-group">
            <label htmlFor={`dvc_${field}`}>{label} *</label>
            <select
              id={`dvc_${field}`}
              value={value[field]}
              onChange={event => update(field, event.target.value)}
              required={required}
            >
              <option value="">Select...</option>
              <option value="false">No</option>
              <option value="true">Yes</option>
            </select>
          </div>
        ))}
      </div>

      {value.warning_lights_messages === 'true' && (
        <div className="form-group">
          <label htmlFor="dvc_warning_notes">Warning notes *</label>
          <textarea
            id="dvc_warning_notes"
            value={value.warning_notes}
            onChange={event => update('warning_notes', event.target.value)}
            rows={2}
            required
          />
        </div>
      )}

      {value.damage === 'true' && (
        <div className="conditional-panel">
          <div className="form-group">
            <label htmlFor="dvc_damage_description">Damage description *</label>
            <textarea
              id="dvc_damage_description"
              value={value.damage_description}
              onChange={event => update('damage_description', event.target.value)}
              rows={2}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="dvc_damage_photo">Damage photos *</label>
            <input
              id="dvc_damage_photo"
              type="file"
              accept="image/*"
              multiple
              onChange={event => handleDamagePhotoChange(event.target.files, event.target)}
              required={(value.damage_photo_attachments || []).length === 0}
            />
            <div className="modal-hint">
              Add as many photos as needed — each pick adds to the set.
            </div>
            {sendingPhotos > 0 && (
              <div className="modal-hint" role="status">
                {sendingPhotos === 1 ? 'Sending 1 photo...' : `Sending ${sendingPhotos} photos...`}
              </div>
            )}
            {(value.damage_photo_attachments || []).length > 0 && (
              <div className="robot-check-photos">
                {value.damage_photo_attachments.map((att, idx) => (
                  <DamagePhotoThumb
                    key={att.file_id || att.client_ref || att.attachment_id || `${att.name}-${idx}`}
                    att={att}
                    onRemove={() => removeDamagePhoto(idx)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="form-group">
        <label htmlFor="dvc_additional_information">Additional information</label>
        <textarea
          id="dvc_additional_information"
          value={value.additional_information}
          onChange={event => update('additional_information', event.target.value)}
          rows={2}
        />
      </div>
    </div>
  );
}

export default DailyVanCheckSection;
